/**
 * The grant form: person, venue, role (ORDR-94), extracted from `GrantsPanel`
 * unchanged when the ORDR-120 roster pushed that file past 300 lines.
 *
 * PRESENTATIONAL. The panel owns the state and computes `scopes` and `roles`
 * with `grantPolicy.ts`; this file only draws them. The venue select is also
 * what picks the roster's scope, so it stays here, where it always was.
 */

import type { ReactElement } from "react";
import { scopeKey } from "./grantPolicy.js";
import type { CatalogRole, Scope } from "./types.js";

export interface GrantFormProps {
  readonly userId: string;
  readonly scopes: readonly Scope[];
  readonly scopeIndex: number;
  readonly roles: readonly CatalogRole[];
  /** The effective role key, already defaulted by the panel. */
  readonly role: string;
  readonly busy: boolean;
  readonly onUserIdChange: (userId: string) => void;
  readonly onScopeIndexChange: (index: number) => void;
  readonly onRoleChange: (roleKey: string) => void;
  readonly onSubmit: () => void;
}

export function GrantForm({
  userId,
  scopes,
  scopeIndex,
  roles,
  role,
  busy,
  onUserIdChange,
  onScopeIndexChange,
  onRoleChange,
  onSubmit,
}: GrantFormProps): ReactElement {
  return (
    <form
      data-testid="grant-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="mb-4 flex flex-col gap-2"
    >
      <label className="text-sm">
        Person
        <input
          value={userId}
          onChange={(event) => onUserIdChange(event.target.value)}
          placeholder="User ID"
          data-testid="grant-user"
          className="mt-1 w-full rounded border border-[var(--color-neutral-300)] p-2"
        />
      </label>

      <label className="text-sm">
        Venue
        <select
          value={String(scopeIndex)}
          onChange={(event) => onScopeIndexChange(Number(event.target.value))}
          data-testid="grant-scope"
          className="mt-1 w-full rounded border border-[var(--color-neutral-300)] p-2"
        >
          {scopes.map((option, index) => (
            <option key={scopeKey(option)} value={index}>
              {scopeKey(option)}
            </option>
          ))}
        </select>
      </label>

      <label className="text-sm">
        Role
        <select
          value={role}
          onChange={(event) => onRoleChange(event.target.value)}
          data-testid="grant-role"
          className="mt-1 w-full rounded border border-[var(--color-neutral-300)] p-2"
        >
          {roles.map((option) => (
            <option key={option.key} value={option.key}>
              {option.name}
            </option>
          ))}
        </select>
      </label>

      <button
        type="submit"
        disabled={busy || role === "" || userId.trim() === ""}
        data-testid="grant-submit"
        className="rounded bg-[var(--color-primary-700)] p-2 text-sm text-white disabled:opacity-60"
      >
        Grant
      </button>
    </form>
  );
}
